import React, { useEffect, useState } from 'react';
import { Users, MapPin, CalendarDays, BadgeCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { tradeLabel } from '@/data/constants';

interface Resp {
  id: string;
  listing_id: string;
  created_at: string;
  listing_title: string;
  company?: string;
  trades?: string[];
  city?: string;
  state?: string;
  licensed?: boolean;
  insured?: boolean;
  verified?: boolean;
}

/**
 * Homeowner-only "Responses" view: which contractors have unlocked each project.
 * Intentionally shows NO lead price, no balance and no contractor billing data —
 * only who is interested so the homeowner can expect their call.
 */
const HomeownerResponses: React.FC = () => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [rows, setRows] = useState<Resp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!profile) return;
      setLoading(true);

      const { data: listings } = await supabase
        .from('mkt_listings')
        .select('id,title')
        .eq('owner_id', profile.id);

      const ids = (listings || []).map((l: { id: string }) => l.id);
      if (!ids.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const { data: purchases } = await supabase
        .from('mkt_lead_purchases')
        .select('id,listing_id,buyer_id,created_at')
        .in('listing_id', ids)
        .order('created_at', { ascending: false });

      // Contractor cards. `mkt_profiles` is no longer world-readable — the new
      // p_profiles_read policy only returns a profile to (a) itself, (b) an
      // admin, or (c) a counterparty linked by an actual lead purchase. A
      // homeowner therefore sees exactly the contractors who unlocked one of
      // THEIR projects and nobody else, so the old "dump every contractor"
      // scrape is dead. We also request a minimal column list — no email,
      // phone, license number or credits — and `is_verified` (which does not
      // exist on the table) has been dropped; it was silently failing the query.
      const buyerIds = [...new Set((purchases || []).map((p: any) => p.buyer_id).filter(Boolean))];
      const { data: buyers } = buyerIds.length
        ? await supabase
            .from('mkt_profiles')
            .select('id,company_name,first_name,last_name,trades,city,state,license_verified,insurance_verified,background_verified')
            .in('id', buyerIds)
        : { data: [] as any[] };


      const map = new Map((buyers || []).map((b: any) => [b.id, b]));
      const titles = new Map((listings || []).map((l: any) => [l.id, l.title]));

      setRows(
        (purchases || []).map((p: any) => {
          const b = map.get(p.buyer_id) || {};
          return {
            id: p.id,
            listing_id: p.listing_id,
            created_at: p.created_at,
            listing_title: titles.get(p.listing_id) || '',
            company: b.company_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || undefined,
            trades: b.trades || [],
            city: b.city,
            state: b.state,
            licensed: !!b.license_verified,
            insured: !!b.insurance_verified,
            verified: !!b.background_verified,

          };
        })
      );
      setLoading(false);
    };
    load();
  }, [profile?.id]);

  return (
    <section className="mt-12">
      <h3 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
        <Users className="h-5 w-5 text-orange-500" />
        {t('dash.responsesTitle')}
      </h3>
      <p className="mt-1 text-sm text-slate-500">{t('dash.responsesSub')}</p>

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          {t('dash.noResponses')}
        </p>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{r.listing_title}</p>
              <h4 className="mt-1 text-base font-extrabold text-slate-900">
                {r.company || t('dash.contractorInterested')}
              </h4>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {(r.trades || []).slice(0, 3).map((tr) => (
                  <span key={tr} className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold uppercase text-white">
                    {tradeLabel(tr, lang)}
                  </span>
                ))}
              </div>

              <div className="mt-3 grid gap-1.5 text-sm text-slate-600">
                {(r.city || r.state) && (
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {[r.city, r.state].filter(Boolean).join(', ')}
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-slate-400" />
                  {t('dash.respondedOn')} {new Date(r.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {r.verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.verified')}
                  </span>
                )}
                {r.licensed && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.licensed')}
                  </span>
                )}
                {r.insured && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.insured')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default HomeownerResponses;
